import './App.css';
import { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import axios from './api/axios';

import NavBar from './components/NavBar';
import MobileNavBar from './components/MobileNavBar';
import Home from './pages/Home';
import About from './pages/About';
import OurProcess from './pages/OurProcess';
import Contact from './pages/Contact';
import NotFoundPage from './pages/NotFoundPage';

import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminMessages from './pages/admin/AdminMessages';
import Admin from './pages/admin/Admin';
import UserProfile from './pages/UserProfile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminPrivateRoute from './utility/AdminPrivateRoute';
import UserPrivateRoute from './utility/UserPrivateRoute';
import BackupCodes from './pages/BackupCodes';
import User from './pages/User';
import AdminProducts from './pages/admin/AdminProducts';
import AdminAddProduct from './pages/admin/AdminAddProduct';
import AdminProductForm from './pages/admin/AdminProductForm';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import OrderSuccess from './pages/OrderSuccess';
import MyOrders from './pages/MyOrders';
import AdminProductDetails from './pages/admin/AdminProductDetails';
import ProductDetails from './pages/ProductDetails';
import AdminTagsCategories from './pages/admin/AdminTagsCategories';
import ViewCategory from './pages/ViewCategory';
import ViewAllProducts from './pages/ViewAllProducts';
import Footer from './components/Footer';
import ScrollToTop from './utility/ScrollToTop';
import AdminOrders from './pages/admin/AdminOrders';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ShippingAndReturns from './pages/ShippingAndReturns';
import VerifyEmail from './pages/VerifyEmail';

import allProductsPageTitle from './assets/allProducts.webp';
import aboutPageTitle from './assets/testWoodsBG3.webp';
import contactPageTitle from './assets/contactBG.webp';
import ourProcessPageTitle from './assets/exoticWood.webp';
import termsPageTitle from './assets/termsConditions.webp';

import logo from './assets/logo.webp';
import AdminAnalytics from './pages/admin/AdminAnalytics';

import knife from './assets/knife.webp';
import mesh from './assets/heroMeshFrame.webp';
import babyKnives from './assets/babyKnives.webp';
import larryInShop from './assets/larryInShop.webp';
import woodsBG from './assets/woodsBG.webp';
import leaves from './assets/heroLeavesFrame.webp';
import AdminContacts from './pages/admin/AdminContacts';
import OrderDetails from './pages/OrderDetails';

//import { useAuth } from "./context/AuthProvider";
//import { useStore } from "./context/StoreContext";

function App() {
  const location = useLocation();

  useEffect(() => {
    const trackPageView = async () => {
      let currentPath = location.pathname;

      // 🚫 Skip admin & user pages
      if (currentPath.startsWith('/admin') || currentPath.startsWith('/user')) {
        return;
      }

      // ⭐ Normalize dynamic paths so analytics aggregate properly
      if (/^\/order-success\/[a-z0-9\-]+$/i.test(currentPath)) {
        currentPath = "/order-success"; // normalize
      }
      if (/^\/order-details\/[a-z0-9\-]+$/i.test(currentPath)) {
        currentPath = "/order-details"; // normalize
      }

      // Prevent double tracking in same session
      const viewedPages = JSON.parse(sessionStorage.getItem('viewedPages') || '[]');

      if (!viewedPages.includes(currentPath)) {
        try {
          await axios.post('/api/public/track-pageview', { path: currentPath });

          viewedPages.push(currentPath);
          sessionStorage.setItem('viewedPages', JSON.stringify(viewedPages));
        } catch (error) {
          console.error('Error tracking page view:', error);
        }
      }
    };

    trackPageView();
  }, [location.pathname]);



  const shouldShowFooter = !(location.pathname.startsWith('/admin') || 
                         location.pathname.startsWith('/checkout'));

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

  //const { loading: authLoading } = useAuth();
  //const { loading: storeLoading } = useStore(); // ✅ Add this line

  //const isLoading = authLoading || storeLoading; // ✅ Combine them

  useEffect(() => {
    const imagesToPreload = [
      babyKnives,
      larryInShop,
      mesh,
      knife,
      leaves
    ];
    imagesToPreload.forEach((imageSrc) => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, []);

  // Show spinner when Auth or StoreContext loading
// if (isLoading) {
//   return (
//     <div className="App">
//       <ScrollToTop />
//       <header role="banner" className="App-header">
//         <div className='header-wrapper'>
//           {windowWidth >= 850 ? <NavBar /> : <MobileNavBar />}
//         </div>
//       </header>
//       <main id="main-content" className="content-wrapper">
//         <div className='page-wrapper'>
//           <div className="loader-wrapper-global" aria-live="assertive" role="status">
//             <div className="spinner"></div>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// }


  return (
    <div className="App">
      <ScrollToTop />
      <header role="banner" className="App-header">
        <div className='header-wrapper'>
          {/*windowWidth >= 785 && <ContactBar />*/}
          {windowWidth >= 850 ? <NavBar /> : <MobileNavBar />}
        </div>
      </header>

      <main id="main-content" className="content-wrapper">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/our-process" element={<OurProcess />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-returns" element={<ShippingAndReturns />} />

          <Route path="/shop-knives/all-knives" element={<ViewAllProducts />} />
          <Route path="/shop-knives/knife/:productId/:productSlug" element={<ProductDetails />} />
          <Route path="/shop-knives/category/:categorySlug" element={<ViewCategory />} />

          <Route path="/shopping-cart" element={<Cart />} />
          <Route path="/secure-checkout" element={<Checkout />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/order-success/:orderUuid" element={<OrderSuccess />} />
          <Route path="/order-details/:orderUuid" element={<OrderDetails />} />

          
          

          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route element={<UserPrivateRoute />}>
            <Route path="/user" element={<User />}>
              <Route index element={<Navigate to="/user/profile" replace />} /> {/* ✅ Redirect /user -> /user/profile */}
              <Route path="profile" element={<UserProfile />} />
              <Route path="backup-codes" element={<BackupCodes />} />
              <Route path="orders" element={<MyOrders />} />
            </Route>
          </Route>

          <Route element={<AdminPrivateRoute />}>
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} /> {/* ✅ Redirect /admin -> /admin/dashboard */}
              <Route path="/admin/*" element={<Admin />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/:productId" element={<AdminProductForm mode="edit"/>} />
                <Route path="products/add" element={<AdminProductForm mode="add"/>} />
                <Route path="tags-categories" element={<AdminTagsCategories />} />
                <Route path="contacts" element={<AdminContacts />} />
              </Route>
          </Route>
          

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {shouldShowFooter && <Footer />}
    </div>
  );
}

export default App;
